/**
 * The builder's dry run: how many rows the draft would change today, which
 * ones and how, and what it would do to one row picked to test it on. The rows
 * to pick from come from their own request, so typing a search does not
 * evaluate the draft again. Asks the server, which reads the rows as the
 * author's board shows them, so nothing is written and the draft need not be
 * saved.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';

import {
  trackerAutomationPreviewQueryOptions,
  trackerAutomationPreviewRowsQueryOptions,
  type PreviewTrackerAutomationDto,
  type TrackerAutomationPreviewRow,
} from '@/entities/tracker-automation';
import { useDebounce } from '@/shared/hooks';

import { listText, writtenKeyLabel, writtenValueText } from '../../lib/automation-runs';
import type { CustomColumn, StatusOption } from '../../lib/useTrackersBoard';

const DRAFT_SETTLE_MS = 400;

interface AutomationPreviewProps {
  trackerId: string;
  /** Pass a stable object: a new one starts a new request. */
  draft: PreviewTrackerAutomationDto;
  columns: CustomColumn[];
  groups: StatusOption[];
}

export function AutomationPreview({ trackerId, draft, columns, groups }: AutomationPreviewProps) {
  const { t } = useTranslation('trackers');
  const [rowSearch, setRowSearch] = useState('');
  const [rowRef, setRowRef] = useState<string | undefined>(undefined);
  // Memoised so the debounce sees one value per real change, not one per render.
  const request = useMemo(() => ({ ...draft, rowRef }), [draft, rowRef]);
  const settled = useDebounce(request, DRAFT_SETTLE_MS);
  const { data, isFetching, isError } = useQuery(
    trackerAutomationPreviewQueryOptions(trackerId, settled),
  );
  const search = useDebounce(rowSearch.trim(), DRAFT_SETTLE_MS);
  const { data: candidates } = useQuery(
    trackerAutomationPreviewRowsQueryOptions(trackerId, search),
  );

  // A picked row the board stops showing comes back as no row; let go of it so
  // the picker shows its placeholder instead of a blank value.
  const pickedRowGone = Boolean(settled.rowRef && data && data.row === null);
  useEffect(() => {
    if (pickedRowGone) setRowRef(undefined);
  }, [pickedRowGone]);

  const describe = (row: TrackerAutomationPreviewRow) =>
    listText(
      row.changes.map((change) =>
        t('trackersBoard.automationPreview.change', {
          column: writtenKeyLabel(change.columnId, columns),
          before: writtenValueText(change.columnId, change.before, columns, groups),
          after: writtenValueText(change.columnId, change.after, columns, groups),
        }),
      ),
    );

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <p className="text-sm text-foreground" role="status">
        {isError
          ? t('trackersBoard.automationPreview.failed')
          : !data || isFetching
            ? t('trackersBoard.automationsPanel.countingRows')
            : data.rowCount > 0
              ? t('trackersBoard.automationPreview.wouldChangeToday', { count: data.rowCount })
              : t('trackersBoard.automationPreview.wouldChangeNone')}
      </p>
      {data && data.sample.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {data.sample.map((row) => (
            <li key={row.rowRef}>
              <span className="font-medium text-foreground">
                {row.title ?? t('trackersBoard.automationPreview.untitledRow')}
              </span>
              {t('trackersBoard.automationsPanel.metaSeparator')}
              {describe(row)}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="automation-test-row">
          {t('trackersBoard.automationPreview.testOnARow')}
        </Label>
        <div className="flex gap-2">
          <Input
            aria-label={t('trackersBoard.automationPreview.searchRows')}
            placeholder={t('trackersBoard.automationPreview.searchRows')}
            value={rowSearch}
            onChange={(event) => setRowSearch(event.target.value)}
            className="w-40"
          />
          <Select value={rowRef ?? ''} onValueChange={(next) => setRowRef(next || undefined)}>
            <SelectTrigger id="automation-test-row" className="flex-1">
              <SelectValue placeholder={t('trackersBoard.automationPreview.pickARow')} />
            </SelectTrigger>
            <SelectContent>
              {(candidates?.data ?? []).map((candidate) => (
                <SelectItem key={candidate.rowRef} value={candidate.rowRef}>
                  {candidate.title ?? t('trackersBoard.automationPreview.untitledRow')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {data?.row ? (
          <p className="text-xs text-foreground" role="status">
            {!data.row.passesConditions
              ? t('trackersBoard.automationPreview.rowFailsConditions')
              : data.row.changes.length === 0
                ? t('trackersBoard.automationPreview.rowAlreadyHolds')
                : t('trackersBoard.automationPreview.rowWouldGet', {
                    changes: describe(data.row),
                  })}{' '}
            {data.row.inTriggerState
              ? t('trackersBoard.automationPreview.rowInTriggerState')
              : t('trackersBoard.automationPreview.rowNotInTriggerState')}
          </p>
        ) : null}
      </div>
    </section>
  );
}
